/* eslint-disable local/no-block-disable-of-nested-eslint */
/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
import { ComponentType } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { Picture } from '@/components/wpl/image/src/image';
import { wacToken } from '@/libraries/design/foundations';

import { useHeroState } from './util/hero-context';

type HeroCardProps = {
	readonly eyebrow: string;
	readonly title: string;
	readonly image: string;
	readonly BackgroundImage: ComponentType;
	readonly idx: number;
	readonly label: string;
};

export const HeroCard = ({ eyebrow, title, image, BackgroundImage, idx, label }: HeroCardProps) => {
	const { openModal } = useHeroState();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();

	const handleClick = () => {
		openModal(idx);
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			attributes: {
				name: `atlas-55799-${label}-modal-opened`,
				section: 'hero-section',
			},
		}).fire();
	};

	const eyebrowText = intl.locale === 'en' ? eyebrow : '';

	return (
		<button css={cardStyles} onClick={handleClick}>
			<div css={textStyles}>
				<div css={eyebrowStyles}>{eyebrowText}</div>
				<div css={titleStyles}>{title}</div>
			</div>
			<div css={imageContainer}>
				<BackgroundImage />
				<div css={[pictureStyles]}>
					<Picture
						image={{
							src: image,
							width: 227,
							height: 364,
							alt: '',
							loading: 'eager',
						}}
					/>
				</div>
			</div>
		</button>
	);
};

const cardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	textAlign: 'left',
	padding: '0',
	border: '0',
	borderRadius: '22px',
	maxHeight: '400px',
	minWidth: '260px',
	background: wacToken('color.neutral.light0'),
	boxShadow: token('elevation.shadow.raised'),
	cursor: 'pointer',
	transition: '300ms',
	overflow: 'clip',
	[media.above.lg]: {
		maxWidth: '260px',
		maxHeight: '496px',
		['&:hover']: {
			boxShadow: token('elevation.shadow.overlay'),
			transform: 'translateY(-10px)',
			['svg']: {
				opacity: '1',
			},
			['.chevron']: {
				opacity: '1',
				transform: 'translateX(10px)',
			},
		},
	},
	[media.below.lg]: {
		maxWidth: '245px',
	},
	[media.below.sm]: {
		maxHeight: '320px',
	},
});

const textStyles = css({
	paddingInlineStart: '34px',
});

const eyebrowStyles = css({
	color: wacToken('color.primary.black'),
	fontFamily: 'var(--atlassian-recommended-system-fonts)',
	fontSize: '12px',
	fontWeight: '400',
	lineHeight: '16px',
	letterSpacing: '0.24px',
	textTransform: 'uppercase',
	paddingBlockStart: '29px',
	textWrap: 'nowrap',
	[media.below.sm]: {
		lineHeight: '13px',
		width: '142px',
	},
});

const titleStyles = css({
	display: 'flex',
	width: '158px',
	color: wacToken('color.primary.black'),
	fontFamily: 'var(--atlassian-recommended-system-fonts)',
	fontSize: '24px',
	fontWeight: '700',
	lineHeight: '30px',
	paddingBlockStart: '7px',
	wordWrap: 'break-word',
	hyphens: 'auto',
	justifyContent: 'space-between',
	[media.below.lg]: {
		width: '142px',
		height: '58px',
		marginBlockEnd: '24px',
		fontSize: '20px',
	},
	[media.below.sm]: {
		color: wacToken('color.primary.black'),
		fontWeight: '600',
		lineHeight: '28px',
	},
});

const imageContainer = css({
	position: 'relative',
	overflowY: 'clip',
	maxHeight: '400px',
});

const pictureStyles = css({
	borderRadius: '22px',
	img: {
		marginInlineStart: '34px',
		position: 'absolute',
		insetBlockEnd: '0',
		[media.below.lg]: {
			insetBlockStart: '0',
		},
	},
});
